import { Fragment, useEffect, useRef, useState } from 'react';
import ContentLoader from 'react-content-loader';
import { useNavigate, useParams } from 'react-router-dom';
import Container from '../../../../core/components/atoms/Container';
import Lesson from '../../../../core/components/organism/Lesson';
import { useMostraSeuAprovaTrigger } from '../../../../core/components/organism/MostraSeuAprovaModal/useMostraSeuAprovaTrigger';
import { markLessonFinish } from '../../../../core/queries/lessons';
import { getViewedFromResponse } from '../../../../core/models/contentView';
import { LessonBreadcrumbsHeader } from '../../components/LessonHeader/inedx';
import { useContentLesson } from '../../hooks/useContentLesson';
import { useLessonContentStore } from '../../stores/lessonContentStore';
import { useLesson } from '../../hooks/useLesson';
import NoContent from '../../../../core/components/templates/NoContent/index';
import { useLessonStore } from '../../stores/lessonStore';
import LockUniversitario from '../../../../core/components/organism/UniversitarioLock';
import { PopUpModal } from '../../../Gamification/components/Playing/PopUpModal';
import { splitContentList } from '../../utils/contentHelper';
import { useAccess } from '../../../../core/accessControl/useAccess';
import { useUserStore } from '../../../../modules/Auth/stores/authStore';
import {
  getUserPermissions,
  hasExactPermission,
} from '../../../../core/accessControl/utils';
import { getUniversitarioDisciplinePermission } from '../../../../core/utils/discipline';

export default function ContentLesson() {
  const [showModalScore, setShowModalScore] = useState(false);

  // Aqui em cima, antes dos returns condicionais deste componente: hooks
  // precisam rodar na mesma ordem em toda renderização.
  const { fire: fireMostraSeuAprova } = useMostraSeuAprovaTrigger();
  // Guarda contra repetição na mesma aula: rever o trecho final faz o player
  // emitir o fim de novo.
  const firedLessonSlug = useRef<string | null>(null);

  const { discipline, module, subject, slug } = useParams();
  const navigate = useNavigate();

  const { isLoading, isError } = useContentLesson(slug || '');

  const { mutate } = useLesson();

  const {
    lesson,
    currentLessonIndex,
    allLessons,
    nextLink,
    previousLink,
    setNextAndPreviousLesson,
  } = useLessonContentStore();

  const { setLessons, lessons } = useLessonStore();
  const { isFreemium, isContentLocked } = useAccess();
  const { user } = useUserStore();
  useEffect(() => {
    if (module) {
      mutate(module);
    }
  }, [module]);

  useEffect(() => {
    const baseLink = `/disciplinas/${discipline}/modulo/${module}/assunto/${subject}/videoaula`;
    setNextAndPreviousLesson(baseLink);
  }, [currentLessonIndex, allLessons]);

  if (isLoading) {
    return (
      <Container direction='col' gap='xxl' className='lg:py-18 py-24'>
        <ContentLoader />
      </Container>
    );
  }

  if (isError) {
    const url = `/disciplinas/${discipline}/modulo/${module}/tipo/videoaula`;

    return <NoContent url={url} />;
  }

  if (!lesson) {
    return <></>;
  }

  const universitarioPermission = getUniversitarioDisciplinePermission(
    lesson.ancestors.area.slug,
  );

  if (
    universitarioPermission &&
    !hasExactPermission(getUserPermissions(user), universitarioPermission)
  ) {
    return <LockUniversitario />;
  }

  const nextLesson = () => {
    if (nextLink) {
      navigate(nextLink);
    }
  };

  const previousLesson = () => {
    if (previousLink) {
      navigate(previousLink);
    }
  };

  const {
    slug: contentSlug,
    title,
    ancestors,
    teacher,
    timeline,
    duration,
    sambatech_token,
    vimeoID,
    defaultPlayer,
    embedUrl,
    pdfSummaryUri,
    notesPdfUri,
    captionUri,
    favorite,
    review,
    _id,
    subjects,
  } = lesson;

  function dispatchSetContentAsDoneEvent() {
    const setContentAsDone = new CustomEvent('setContentAsDone', {
      detail: {
        contentId: _id,
      },
    });
    window.dispatchEvent(setContentAsDone);
  }

  const onFinish = async (origin?: 'virtual' | 'ended') => {
    if (slug) {
      // Automático (assistiu a aula): sempre marca como vista (viewed: true),
      // para não desmarcar ao reassistir uma aula já concluída.
      const result = await markLessonFinish(slug, {
        origin: 'free_browsing',
        viewed: true,
      });
      dispatchSetContentAsDoneEvent();

      // Toggle: usa o status retornado como fonte da verdade.
      const viewed = getViewedFromResponse(result);
      const lessonIndex = lessons.findIndex((it) => it.slug === slug);

      if (lessonIndex !== -1) {
        lessons[lessonIndex].done = viewed;
        setLessons(lessons);
      }

      // Campanha #mostraseuaprova, só quando o vídeo termina de verdade. A
      // conclusão virtual chega por volta dos 80% e marca a aula como
      // assistida, mas o aluno ainda está assistindo — o modal cobriria o
      // player. Registrada como `nav-livre` para o relatório separar a
      // navegação livre dos cursos, embora o texto exibido seja o mesmo.
      //
      // Sai junto com a campanha — ver
      // core/components/organism/MostraSeuAprovaModal/README.md.
      if (origin === 'ended' && firedLessonSlug.current !== slug) {
        firedLessonSlug.current = slug;
        fireMostraSeuAprova('nav-livre');
      }

      return result;
    }
  };

  // Quando o default é o player novo (aprova), o player antigo ainda precisa
  // funcionar via toggle. Como 'aprova' não é um tipo válido para o <Player>
  // clássico, derivamos um player legado a partir dos tokens disponíveis.
  const legacyPlayerType =
    defaultPlayer === 'aprova'
      ? vimeoID
        ? 'vimeo'
        : 'sambatech'
      : defaultPlayer;
  const legacyToken =
    legacyPlayerType === 'vimeo' ? vimeoID : sambatech_token;

  return (
    <Fragment>
      <Container direction='col' gap='xxl' className='lg:pb-10'>
        <LessonBreadcrumbsHeader />
        <Lesson
          id={_id}
          module={module}
          isFavorite={favorite}
          review={review}
          title={title}
          discipline={ancestors.area.slug}
          time={duration}
          teacher={(teacher || [])
            .map((it: { name: string }) => it.name)
            .join(', ')}
          timeline={timeline}
          playerType={legacyPlayerType}
          token={legacyToken}
          embedUrl={embedUrl}
          defaultPlayer={defaultPlayer}
          pdfSummary={pdfSummaryUri}
          notesPdfUri={notesPdfUri}
          captionUri={captionUri}
          onFinish={onFinish}
          contentSlug={contentSlug}
          viewOrigin='free_browsing'
          subjects={subjects}
          subjectRelated={subjects[0].slug}
          isDisciplines
          showQuiz={true}
          onNext={nextLink ? nextLesson : undefined}
          onPrevious={previousLink ? previousLesson : undefined}
          locked={isContentLocked(lesson)}
          isFreemiumUser={isFreemium}
        />
      </Container>

      <PopUpModal
        isOpen={showModalScore}
        onClose={() => setShowModalScore(false)}
        score={5}
      />
    </Fragment>
  );
}
